import { Component, ChangeDetectionStrategy, inject, computed, signal, viewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { Actions, ofType } from "@ngrx/effects";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";

import { DynamicDialogRef } from "primeng/dynamicdialog";
import { ButtonModule } from "primeng/button";
import { StepperModule } from "primeng/stepper";
import { RadioButtonModule } from "primeng/radiobutton";

import { QuoteStatus } from "src/api-service";
import { QuoteActions } from "src/app/store/pages/quote/quote.actions";
import { selectQuoteState } from "src/app/store/pages/quote/quote.selectors";
import { selectNeedsCreditCheckStep } from "src/app/store/application/data/credit-check/quote/quote-credit-check.selectors";
import { CreditCheckStepComponent } from "../credit-check-step/credit-check-step.component";
import { CreateOrderDialogComponent } from "../create-order-dialog/create-order-dialog.component";
import { CreateOrderSummaryComponent } from "../create-order-summary/create-order-summary.component";
import { SendOrderToReviewDialogComponent } from "../send-order-to-review-dialog/send-order-to-review-dialog.component";

/** Where the quote goes from the confirmation step. */
export type OrderDestination = "review" | "order";

/**
 * "Skapa order" as one dialog with a stepper.
 *
 * The old flow asked "review or order?" in its own popup before showing any detail. Here both
 * destinations are buttons on the final step instead, so the choice is made with the credit result
 * and the comments already on screen — and one popup disappears.
 *
 * Sending for review is only offered from Sent; from UnderReview the quote is already in review and
 * the only way forward is to create the order.
 */
@Component({
    selector: "app-create-order-stepper",
    templateUrl: "./create-order-stepper.component.html",
    styleUrl: "../quote-flow-stepper.scss",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        ButtonModule,
        StepperModule,
        RadioButtonModule,
        TranslateModule,
        CreditCheckStepComponent,
        CreateOrderDialogComponent,
        CreateOrderSummaryComponent,
        SendOrderToReviewDialogComponent,
    ],
})
export class CreateOrderStepperComponent {

    private readonly ref = inject(DynamicDialogRef);
    private readonly store = inject(Store);
    private readonly actions$ = inject(Actions);

    private readonly orderPanel = viewChild(CreateOrderDialogComponent);
    private readonly reviewPanel = viewChild(SendOrderToReviewDialogComponent);

    // =========================================================================
    // WHICH STEPS APPLY
    //
    // Captured once on open, so resolving the credit step doesn't renumber the stepper underneath
    // the user mid-flow.
    // =========================================================================
    public readonly showCreditStep = signal(
        this.store.selectSignal(selectNeedsCreditCheckStep)() &&
        this.store.selectSignal(selectQuoteState)()?.status !== QuoteStatus.UnderReview,
    );

    public readonly creditStepIndex = computed(() => 1);
    public readonly confirmStepIndex = computed(() => (this.showCreditStep() ? 2 : 1));
    public readonly showStepHeader = computed(() => this.showCreditStep());

    public readonly activeStep = signal(1);

    /** Review is a destination only while the quote is still Sent. */
    public readonly canSendToReview = computed(() =>
        this.store.selectSignal(selectQuoteState)()?.status === QuoteStatus.Sent,
    );

    /**
     * Which destination the user has picked, or null until they pick one.
     *
     * Deliberately unset rather than defaulted to review. The two routes are not interchangeable —
     * one goes through sales support, the other writes straight into Business Central — so this is
     * a decision to make, not a default to accept. A preselected option is answered by pressing the
     * primary button without ever reading the question.
     *
     * Only ever null while canSendToReview() is true; without the review option there is no choice
     * to make and the order path is the only destination.
     */
    public readonly destination = signal<OrderDestination | null>(null);
    public readonly creditCheckOK = signal(false);

    /** The destination that will actually be carried out, once a choice is no longer pending. */
    private readonly effectiveDestination = computed<OrderDestination>(() =>
        this.canSendToReview() ? (this.destination() ?? "review") : "order",
    );

    public readonly confirmLabelKey = computed(() =>
        this.effectiveDestination() === "review"
            ? "REVIEW_OR_CREATE_ORDER.SEND_TO_REVIEW"
            : "REVIEW_OR_CREATE_ORDER.CREATE_ORDER_BTN",
    );

    /** The primary is inert until the question above it has been answered. */
    public readonly hasChosenDestination = computed(() =>
        !this.canSendToReview() || this.destination() !== null,
    );

    /**
     * Each destination keeps its own gate — sales-support validation can block review while the
     * order path stays open, so the button reflects only the selected one.
     */
    public readonly canConfirmSelected = computed(() => {
        if (!this.hasChosenDestination()) return false;

        return this.effectiveDestination() === "review"
            ? (this.reviewPanel()?.canConfirm() ?? false)
            : (this.orderPanel()?.canConfirm() ?? false);
    });

    constructor() {
        // Both destinations dispatch from their embedded panel; close once either is accepted.
        this.actions$.pipe(
            ofType(QuoteActions.createOrder, QuoteActions.updateToReadyForReview),
            takeUntilDestroyed(),
        ).subscribe(() => this.ref.close());
    }

    // =========================================================================
    // NAVIGATION
    // =========================================================================
    public advanceFrom(step: number): void {
        this.activeStep.set(step + 1);
    }

    public confirmSelected(): void {
        if (!this.hasChosenDestination()) return;

        if (this.effectiveDestination() === "review") {
            this.reviewPanel()?.onConfirm();
            return;
        }
        this.orderPanel()?.onConfirm();
    }

    public cancel(): void {
        this.ref.close();
    }
}
